import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Shield, 
  Activity, 
  AlertTriangle, 
  Clock,
  User,
  Filter,
  RefreshCw,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useLanguage } from '@/LanguageContext';

const AuditLogs = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAction, setSelectedAction] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 20;

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchAuditLogs();
    fetchStats();
    fetchActions();
  }, [page, selectedAction]);

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      let url = `${BACKEND_URL}/api/audit-logs?page=${page}&page_size=${pageSize}`;
      if (selectedAction) {
        url += `&action=${selectedAction}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs);
        setTotal(data.total);
      }
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/audit-logs/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchActions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/audit-logs/actions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setActions(data.actions);
      }
    } catch (error) {
      console.error('Error fetching actions:', error);
    }
  };

  const getActionColor = (action) => {
    const colors = {
      'login': 'bg-green-100 text-green-800',
      'login_failed': 'bg-red-100 text-red-800',
      'register': 'bg-blue-100 text-blue-800',
      'create_child': 'bg-purple-100 text-purple-800',
      'create_screening': 'bg-yellow-100 text-yellow-800',
      'generate_intervention': 'bg-indigo-100 text-indigo-800',
      'bulk_upload': 'bg-orange-100 text-orange-800'
    };
    return colors[action] || 'bg-gray-100 text-gray-800';
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAF9]" data-testid="audit-logs-page">
      <Navbar />
      
      <main className="flex-1 p-6 max-w-7xl mx-auto w-full">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/dashboard/admin')}
            className="flex items-center space-x-2 text-[#0F4C81] font-semibold mb-4 hover:underline"
            data-testid="back-button"
          >
            <ArrowLeft size={20} />
            <span>{t('common.back')}</span>
          </button>
          
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-[#0F4C81] rounded-xl flex items-center justify-center">
              <Shield className="text-white" size={24} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#111827] font-['Nunito']">
                {t('admin.auditLogs')}
              </h1>
              <p className="text-[#6B7280]">{t('admin.auditLogsDescription')}</p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Activity className="text-blue-600" size={20} />
                </div>
                <div>
                  <p className="text-sm text-[#6B7280]">{t('admin.totalLogs')}</p>
                  <p className="text-2xl font-bold text-[#111827]">{stats.total_logs}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Clock className="text-green-600" size={20} />
                </div>
                <div>
                  <p className="text-sm text-[#6B7280]">{t('admin.recentActivity')}</p>
                  <p className="text-2xl font-bold text-[#111827]">{stats.recent_activity_24h}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="text-red-600" size={20} />
                </div>
                <div>
                  <p className="text-sm text-[#6B7280]">{t('admin.failedLogins')}</p>
                  <p className="text-2xl font-bold text-[#111827]">{stats.failed_login_attempts}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center space-x-2">
              <Filter size={18} className="text-[#6B7280]" />
              <span className="text-sm font-semibold text-[#111827]">{t('admin.filterByAction')}</span>
            </div>
            
            <select
              value={selectedAction}
              onChange={(e) => { setSelectedAction(e.target.value); setPage(1); }}
              className="px-4 py-2 border border-slate-200 rounded-lg focus:border-[#0F4C81] outline-none"
              data-testid="action-filter"
            >
              <option value="">{t('admin.allActions')}</option>
              {actions.map((action) => (
                <option key={action} value={action}>{action}</option>
              ))}
            </select>
            
            <button
              onClick={() => { fetchAuditLogs(); fetchStats(); }}
              className="flex items-center space-x-2 px-4 py-2 bg-[#0F4C81] text-white rounded-lg hover:bg-[#0d3d6b] transition-colors"
              data-testid="refresh-button"
            >
              <RefreshCw size={16} />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* Logs Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-[#6B7280]">{t('common.loading')}</div>
          ) : logs.length === 0 ? (
            <div className="p-8 text-center text-[#6B7280]">{t('admin.noAuditLogs')}</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#F3F4F6]">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-[#111827]">{t('admin.timestamp')}</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-[#111827]">{t('admin.action')}</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-[#111827]">{t('admin.user')}</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-[#111827]">{t('admin.details')}</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-[#111827]">{t('admin.ipAddress')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {logs.map((log) => (
                      <tr key={log.id} className="hover:bg-[#FAFAF9]" data-testid="audit-log-row">
                        <td className="px-4 py-3 text-sm text-[#6B7280]">
                          {formatTimestamp(log.timestamp)}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getActionColor(log.action)}`}>
                            {log.action}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 bg-[#E0E7FF] rounded-full flex items-center justify-center">
                              <User size={14} className="text-[#4F46E5]" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-[#111827]">{log.user_email}</p>
                              <p className="text-xs text-[#6B7280]">{log.user_role}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-[#6B7280] max-w-xs truncate">
                          {log.details ? JSON.stringify(log.details) : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-[#6B7280]">
                          {log.ip_address || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                <p className="text-sm text-[#6B7280]">
                  Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, total)} of {total}
                </p>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 rounded-lg border border-slate-200 hover:bg-[#F3F4F6] disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="prev-page"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className="text-sm text-[#111827]">
                    Page {page} of {totalPages || 1}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="p-2 rounded-lg border border-slate-200 hover:bg-[#F3F4F6] disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="next-page"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default AuditLogs;
